import { Box, BoxProps, Chip, paletteColors, Typography, TypographyProps } from '@evaneos/b2b-ui';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { VisuallyHidden } from '../../shared/components/VisuallyHidden';

import { EditableValue } from './EditableValue';
import { EditableGroup } from './EditableGroup';

type DefinitionListComponentProps = {
    ariaLabel?: string;
    children: ReactNode;
} & BoxProps;

function DefinitionListComponent({
    ariaLabel,
    children,
    ...boxProps
}: DefinitionListComponentProps) {
    return (
        <Box
            component="dl"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                m: 0,
                ...boxProps.sx,
            }}
            aria-label={ariaLabel}
            {...boxProps}
        >
            {children}
        </Box>
    );
}

type ItemProps = {
    children: ReactNode;
} & BoxProps;

function Item({ children, ...boxProps }: ItemProps) {
    return (
        <Box
            {...boxProps}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                minHeight: 32,
                ...boxProps.sx,
            }}
        >
            {children}
        </Box>
    );
}

type LabelProps = {
    id: string;
    children: ReactNode;
    icon?: ReactNode;
    iconOnly?: boolean;
    isNew?: boolean;
} & Omit<TypographyProps, 'component'>;

function Label({
    id,
    children,
    icon,
    iconOnly = false,
    isNew = false,
    ...typographyProps
}: LabelProps) {
    const { formatMessage } = useIntl();
    const textContent = iconOnly ? (
        <VisuallyHidden>{children}</VisuallyHidden>
    ) : (
        <Box
            component="span"
            sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: icon && !isNew ? 1 : undefined,
                minWidth: 0,
            }}
        >
            {children}
        </Box>
    );

    return (
        <Typography
            component="dt"
            id={id}
            variant="body2"
            color="text.secondary"
            sx={{
                width: iconOnly ? 'auto' : 150,
                flexShrink: 0,
                py: iconOnly ? 1 : 0.75,
                display: icon ? 'flex' : undefined,
                gap: icon ? 0.5 : undefined,
                alignItems: icon ? 'center' : undefined,
                ...typographyProps.sx,
            }}
            {...typographyProps}
        >
            {icon}
            {textContent}
            {isNew && (
                <Chip
                    component="strong"
                    size="small"
                    data-testid="label-new-tag"
                    label={formatMessage({ id: 'global.new' })}
                    variant="rounded"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: 'background.information',
                        color: paletteColors.blue[700],
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                        height: 'auto',
                        py: 0.25,
                        ml: iconOnly ? 0 : 0.5,
                        position: 'relative',
                        top: iconOnly ? 0 : '1px',
                    }}
                />
            )}
        </Typography>
    );
}

type StaticValueProps = {
    children: ReactNode;
} & Omit<BoxProps, 'component'>;

function StaticValue({ children, ...boxProps }: StaticValueProps) {
    return (
        <Box
            component="dd"
            sx={{
                flex: 1,
                m: 0,
                padding: 0.5,
                minHeight: '3.2rem',
                alignContent: 'center',
                ...boxProps.sx,
            }}
            {...boxProps}
        >
            {children}
        </Box>
    );
}

export const DefinitionList = Object.assign(DefinitionListComponent, {
    Item,
    Label,
    StaticValue,
    EditableValue,
    EditableGroup,
});
